import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChatHeaderActions.module.css';

interface ChatHeaderActionsProps {
  onHistory: () => void;
  onNew: () => void;
  /** An answer is streaming, or the chart of this conversation is not known yet. */
  disabled: boolean;
}

/** Clock with a back-arrow: the conversation history. */
function HistoryIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
      <path d="M3.5 4.5v4.2h4.2" />
      <path d="M12 7.6V12l3 1.9" />
    </svg>
  );
}

function PlusIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** The two icon buttons on the right of the chat header. The history button takes a ref (focus returns to it when the sheet closes). */
export const ChatHeaderActions = forwardRef<HTMLButtonElement, ChatHeaderActionsProps>(function ChatHeaderActions(
  { onHistory, onNew, disabled },
  historyRef,
) {
  const { t } = useTranslation('home');
  return (
    <>
      <button
        ref={historyRef}
        type="button"
        className={styles.iconBtn}
        onClick={onHistory}
        disabled={disabled}
        aria-label={t('chat.history.title')}
        data-testid="chat-history"
      >
        <HistoryIcon />
      </button>
      <button
        type="button"
        className={styles.iconBtn}
        onClick={onNew}
        disabled={disabled}
        aria-label={t('chat.new')}
        data-testid="chat-new"
      >
        <PlusIcon />
      </button>
    </>
  );
});
